import {
  MarketingLayout, Eyebrow, Display, SectionTitle, FocalSentence, Body,
  PrimaryCTA, SecondaryLink, Section, Hairline,
} from "./MarketingLayout";

const ROOMS = [
  { name: "Today",              line: "Your morning brief in one calm sentence." },
  { name: "The Family Room",    line: "Every resident quietly held together." },
  { name: "The Kitchen",        line: "What are we eating? Answered in seconds." },
  { name: "The Pantry",         line: "What the household needs, without a paper list." },
  { name: "The Study",          line: "Bills, savings and quiet reassurance." },
  { name: "The Planning Room",  line: "The family diary — one week at a glance." },
  { name: "The Wellness Room",  line: "Who needs a check-in today." },
  { name: "The Library",        line: "Every document you never want to lose." },
  { name: "The Home",           line: "Chores, cleaning and what needs mending." },
  { name: "The Keepsake Book",  line: "Small memories worth holding on to." },
];

const REASONS = [
  { title: "It doesn't ask you to think like a project manager.",     body: "No labels. No tags. No boards to maintain. Every room is designed the way families already think about their home." },
  { title: "Empty is not a failure.",     body: "Every room ships almost empty. It fills gently as you live your life — never demanding, never guilt-tripping." },
  { title: "Nothing shouts for attention.",     body: "Warm ivory canvas. Fraunces where it matters. Inter where it counts. Numbers never scream. Colour only orients, never decorates." },
  { title: "Ara notices what no-one has time to.",     body: "Not a chatbot. A quiet chief of staff that leans in only when something deserves attention." },
];

export default function HomePage() {
  return (
    <MarketingLayout testid="home-page">
      {/* ── Hero ────────────────────────────────────────────────── */}
      <Section testid="home-hero" className="pt-24 sm:pt-32 pb-16 sm:pb-24">
        <div className="max-w-[820px]">
          <Eyebrow>ARA · Household Operating System</Eyebrow>
          <Display className="mt-6">
            The operating system for running your home.
          </Display>
          <FocalSentence className="mt-8 max-w-[42ch]">
            Finally, something that understands the mental load of family life.
          </FocalSentence>
          <Body className="mt-6 max-w-[52ch]">
            ARA holds the meals, money, documents, appointments and quiet daily
            worries a household carries — so no one person has to hold all of it alone.
          </Body>
          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
            <PrimaryCTA to="/join-beta" testid="home-hero-cta">Join the waitlist →</PrimaryCTA>
            <SecondaryLink to="/how-it-works" testid="home-hero-how">See how it works</SecondaryLink>
          </div>
        </div>
      </Section>

      <Hairline />

      {/* ── The problem ────────────────────────────────────────── */}
      <Section testid="home-problem">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-x-16 gap-y-8">
          <div>
            <Eyebrow>The mental load</Eyebrow>
          </div>
          <div className="max-w-[62ch]">
            <SectionTitle>
              Modern households run on someone’s silent, invisible list.
            </SectionTitle>
            <Body className="mt-8">
              It’s the school notice you must remember. The birthday next week.
              The dentist for the eight-year-old. Insurance renewing on Friday.
              The recipes you’ve made a hundred times but somehow can’t find.
              The bill you meant to pay. The tap that’s still dripping.
            </Body>
            <Body className="mt-4">
              This list has no beginning and no end. It sits behind everything
              else you’re trying to do — and one person, quietly, holds it all.
            </Body>
            <FocalSentence className="mt-8">
              Modern software makes them feel busier, not calmer.
            </FocalSentence>
          </div>
        </div>
      </Section>

      <Hairline />

      {/* ── The solution ───────────────────────────────────────── */}
      <Section testid="home-solution">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-x-16 gap-y-8">
          <div>
            <Eyebrow>The idea</Eyebrow>
          </div>
          <div className="max-w-[62ch]">
            <SectionTitle>
              A residence, quietly organised, always one step ahead.
            </SectionTitle>
            <Body className="mt-8">
              ARA is not a productivity app. It’s a warm, private operating
              system for your household — a set of rooms your family walks
              into instead of a set of tabs you have to manage.
            </Body>
            <Body className="mt-4">
              Each room answers one question in one breath. Nothing shouts.
              Nothing decorates. Everything supports the way your family
              already lives.
            </Body>
          </div>
        </div>
      </Section>

      <Hairline />

      {/* ── The Residence walkthrough ──────────────────────────── */}
      <Section testid="home-rooms">
        <Eyebrow>The Residence</Eyebrow>
        <SectionTitle className="mt-5 max-w-[24ch]">
          Rooms, not a dashboard.
        </SectionTitle>
        <Body className="mt-6 max-w-[52ch]">
          A room for every part of family life. Move
          between them the way you move through a home.
        </Body>
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-x-16 gap-y-12">
          {ROOMS.map(room => (
            <div key={room.name}>
              <p
                className="text-[20px] leading-tight"
                style={{ fontFamily: "var(--ara-font-display, 'Fraunces'), 'Times New Roman', serif", color: 'var(--ara-ink, #1F1F1F)', fontWeight: 400 }}
              >
                {room.name}
              </p>
              <Body className="mt-2">{room.line}</Body>
            </div>
          ))}
        </div>
      </Section>

      <Hairline />

      {/* ── Why families love it ───────────────────────────────── */}
      <Section testid="home-reasons">
        <Eyebrow>Why families choose ARA</Eyebrow>
        <SectionTitle className="mt-5 max-w-[26ch]">
          Built for family life. Not for productivity.
        </SectionTitle>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14">
          {REASONS.map((r, i) => (
            <div key={i}>
              <p
                className="text-[19px] sm:text-[21px] leading-[1.25] tracking-[-0.01em]"
                style={{ fontFamily: "var(--ara-font-display, 'Fraunces'), 'Times New Roman', serif", color: 'var(--ara-ink, #1F1F1F)', fontWeight: 400 }}
              >
                {r.title}
              </p>
              <Body className="mt-4 max-w-[42ch]">{r.body}</Body>
            </div>
          ))}
        </div>
      </Section>

      <Hairline />

      {/* ── Privacy and trust ──────────────────────────────────── */}
      <Section testid="home-trust">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-x-16 gap-y-8">
          <div>
            <Eyebrow>Privacy</Eyebrow>
          </div>
          <div className="max-w-[62ch]">
            <SectionTitle>
              Your household is yours.
            </SectionTitle>
            <Body className="mt-8">
              ARA is private by default. Only the people you invite ever see
              your rooms. We never sell your data. We never advertise inside
              the Residence. We never use family life to train third-party AI.
            </Body>
            <Body className="mt-4">
              Money and Documents are held behind a household PIN. Everything
              is encrypted at rest.
            </Body>
          </div>
        </div>
      </Section>

      <Hairline />

      {/* ── Coming soon ────────────────────────────────────────── */}
      <Section testid="home-coming-soon">
        <div className="max-w-[62ch]">
          <Eyebrow>Coming soon</Eyebrow>
          <FocalSentence className="mt-6">
            Ara will soon become your household Chief of Staff.
          </FocalSentence>
          <Body className="mt-6">
            Automation. Bank sync. Email intelligence. Calendar coordination.
            All arriving quietly, room by room, once the foundation is trusted.
          </Body>
        </div>
      </Section>

      {/* ── Final CTA ──────────────────────────────────────────── */}
      <Section testid="home-final-cta" className="pb-32">
        <div className="rounded-[24px] px-8 sm:px-14 py-16 sm:py-20 text-center" style={{ border: '1px solid var(--ara-line, rgba(31,31,31,0.08))' }}>
          <Eyebrow>Start your Residence</Eyebrow>
          <div className="mt-6">
            <SectionTitle className="max-w-[22ch] mx-auto">
              A calmer household begins with one small step.
            </SectionTitle>
          </div>
          <Body className="mt-6 max-w-[42ch] mx-auto">
            Founding Members shape the Residence as it grows. Early access
            invitations are released in small groups.
          </Body>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
            <PrimaryCTA to="/join-beta" testid="home-final-cta-btn">Join the waitlist →</PrimaryCTA>
            <SecondaryLink to="/pricing">See pricing</SecondaryLink>
          </div>
        </div>
      </Section>
    </MarketingLayout>
  );
}
